import { DownOutlined } from '@ant-design/icons'
import type { MenuProps, TableProps } from 'antd'
import { Checkbox, Dropdown } from 'antd'
import type { SorterResult, SortOrder, TablePaginationConfig } from 'antd/es/table/interface'
import { useI18n } from 'context/I18nProvider'
import * as React from 'react'

interface Props {
  defaultPageSize?: number
  defaultOrderBy?: string
  defaultOrderDir?: SortOrder
  selectAll?: boolean
}

export const useTableState = ({ defaultPageSize = 10, defaultOrderBy, defaultOrderDir, selectAll }: Props) => {
  const [selection, setSelection] = React.useState<React.Key[]>([])
  const [allSelected, setAllSelected] = React.useState<boolean>(false)
  const [current, setCurrent] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(defaultPageSize)
  const [sortedInfo, setSortedInfo] = React.useState<SorterResult<any>>({
    field: defaultOrderBy,
    columnKey: defaultOrderBy,
    column: undefined,
    order: defaultOrderDir || 'ascend',
  })
  const [total, setTotal] = React.useState(0)

  const numberOfPages = React.useMemo(() => Math.ceil(total / pageSize), [total, pageSize])

  const { i18n } = useI18n()

  const items: MenuProps['items'] = [
    {
      key: 'all',
      label: i18n('table.select-all'),
    },
    {
      key: 'none',
      label: i18n('table.select-none'),
    },
  ]

  React.useEffect(() => {
    if (current > numberOfPages) setCurrent(1)
  }, [current, numberOfPages])

  const onClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'all':
        setAllSelected(true)
        setSelection([])
        break
      case 'none':
        setAllSelected(false)
        setSelection([])
        break
      default:
      // pass
    }
  }

  const handleChange = React.useCallback((pagination: TablePaginationConfig, _: any, sorter: any) => {
    if (pagination?.current) setCurrent(pagination.current)
    if (pagination?.pageSize) setPageSize(pagination.pageSize)
    setSortedInfo(sorter as SorterResult<any>)
  }, [])

  const handleSelection: TableProps<any>['rowSelection'] = {
    selectedRowKeys: Object.values(selection).flat() || [],
    preserveSelectedRowKeys: true,
    columnWidth: '3.5rem',
    onChange: (newKeys: React.Key[]) => {
      setSelection(newKeys)
      setAllSelected(false)
    },
    renderCell: (checked: boolean, _record: any, _index: any, originalNode: any) => (
      <Checkbox checked={allSelected || checked} onChange={originalNode?.props?.onChange} />
    ),
    ...(selectAll
      ? {
          columnTitle: (originalNode: any) => (
            <div className="flex justify-between">
              <Checkbox
                checked={allSelected || originalNode?.props?.checked}
                indeterminate={selection.length || originalNode?.props?.indeterminate}
                onChange={originalNode?.props?.onChange}
              />
              <Dropdown menu={{ items, onClick }}>
                <DownOutlined className="cursor-pointer" />
              </Dropdown>
            </div>
          ),
        }
      : {}),
  }

  const resetSelection = React.useCallback(() => setSelection([]), [])

  const sortUrl = React.useMemo(() => {
    if (!sortedInfo.order) return ''
    const order = sortedInfo.order.replace('end', '')

    const field = sortedInfo.columnKey || sortedInfo.field
    return `&order[${field}]=${order}`
  }, [sortedInfo.columnKey, sortedInfo.field, sortedInfo.order])

  return {
    current,
    setCurrent,
    pageSize,
    selection,
    allSelected,
    sortedInfo,
    sortUrl,
    handleChange,
    handleSelection,
    resetSelection,
    setTotalOfItems: setTotal,
    total,
  }
}
